import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, Shield, IdentificationBadge, LockKey, Eye, UsersThree, CheckCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';

export default function SecurityPage() {
  const measures = [
    {
      icon: IdentificationBadge,
      title: 'Verificação de Identidade',
      description: 'Os prestadores podem submeter documento de identificação e selfie para verificação. Perfis verificados recebem um badge visível no perfil.'
    },
    {
      icon: Eye,
      title: 'Avaliações Reais',
      description: 'O sistema de avaliações permite que clientes partilhem experiências reais. Ajudamos a comunidade a identificar os melhores prestadores.'
    },
    {
      icon: LockKey,
      title: 'Dados Protegidos',
      description: 'Os seus dados pessoais são tratados com o máximo cuidado, de acordo com o RGPD. Nunca partilhamos informações com terceiros sem o seu consentimento.'
    },
    {
      icon: UsersThree,
      title: 'Comunidade de Confiança',
      description: 'Incentivamos encontros presenciais antes de qualquer serviço. Conhecer o prestador pessoalmente é o primeiro passo para a segurança do seu animal.'
    }
  ];

  const tips = [
    'Consulte as avaliações e comentários de outros clientes antes de escolher um prestador.',
    'Prefira prestadores com verificação de identidade (badge verificado).',
    'Marque um encontro presencial antes de confiar o seu animal.',
    'Comunique sempre pelo chat da plataforma para manter um registo das conversas.',
    'Informe o prestador sobre necessidades especiais, alergias ou comportamentos do seu animal.',
    'Peça referências ou fotos de outros animais que o prestador já cuidou.',
    'Comece com um serviço curto (ex: um passeio) antes de confiar hospedagens longas.',
    'Certifique-se de que o seu animal tem as vacinas em dia é o chip atualizado.'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6" data-testid="security-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield weight="duotone" className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-outfit">Segurança e Confiança</h1>
              <p className="text-muted-foreground">A sua tranquilidade é a nossa prioridade</p>
            </div>
          </div>

          <section className="my-10">
            <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
              <p className="text-lg leading-relaxed">
                No PasseioPerfeito, trabalhamos para criar um ambiente seguro e transparente. 
                Embora funcionemos como <strong>plataforma de conexão</strong> entre donos de animais e prestadores, 
                implementamos diversas medidas para promover a confiança na comunidade.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">As Nossas Medidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {measures.map((measure) => {
                const Icon = measure.icon;
                return (
                  <Card key={measure.title}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon weight="duotone" className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{measure.title}</h3>
                          <p className="text-sm text-muted-foreground">{measure.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Dicas de Segurança para Clientes</h2>
            <div className="bg-card rounded-2xl border p-6">
              <ul className="space-y-3">
                {tips.map((tip, index) => (
                  <li key={`tip-${index}`} className="flex items-start gap-3">
                    <CheckCircle weight="fill" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
              <h3 className="font-semibold mb-3 text-destructive">Nota Importante</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                O PasseioPerfeito é uma plataforma de conexão e não se responsabiliza por ações, danos ou 
                incumprimentos por parte dos prestadores ou clientes. Recomendamos que tome todas as precauções 
                necessárias e leia os nossos{' '}
                <Link to="/termos" className="text-primary hover:underline">Termos e Condições</Link>
                {' '}antes de utilizar a plataforma.
              </p>
            </div>
          </section>

          <section className="bg-primary text-primary-foreground rounded-2xl p-8 text-center">
            <Shield weight="fill" className="w-10 h-10 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-semibold mb-3">Tem alguma preocupação?</h2>
            <p className="mb-6 opacity-90">
              A nossa equipa está disponível para esclarecer qualquer dúvida sobre segurança.
            </p>
            <Link to="/contactos">
              <Button variant="secondary" className="rounded-full">
                Contactar-nos
              </Button>
            </Link>
          </section>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
